// Paste-to-upload for a file target: a screenshot or a copied image lands on
// the slot without the customer saving it to disk first. Same gesture as
// Nexus's usePasteFiles hook, so staff and customers learn it once.
//
// A paste reaches a target two ways. With focus inside it, the paste event's
// target is the focused element, so the React onPaste returned here sees it
// bubble. With only the pointer over it, the event targets wherever focus sits,
// so a document listener is attached while hovered. Hover wins: a dropzone the
// customer clicked to pick a file keeps focus afterwards, and a paste aimed at
// the slot they are now pointing at must not land back on that one. Whichever
// target takes the paste calls preventDefault, so it never lands on two.
//
// Nothing is filtered by file type here. Each upload path runs its own gate
// and shows why a file was refused; a silent filter would make a wrong-type
// paste look like the shortcut did nothing.

import { useEffect, useRef, useState, type ClipboardEvent as ReactClipboardEvent } from 'react';

const NON_TEXT_INPUT_TYPES = new Set([
  'button',
  'checkbox',
  'color',
  'file',
  'image',
  'radio',
  'range',
  'reset',
  'submit',
]);

// A field where Ctrl+V means "paste text". A file input or a checkbox is not
// one, so a paste while it holds focus still counts as a file paste.
function isTextEntry(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable || target instanceof HTMLTextAreaElement) return true;
  return target instanceof HTMLInputElement && !NON_TEXT_INPUT_TYPES.has(target.type);
}

const pad = (n: number): string => String(n).padStart(2, '0');

// Browsers name every clipboard bitmap "image.png". Stamp it so two pastes
// read as two files in the tile grid and in the Nexus job folder.
function nameClipboardImage(file: File): File {
  const ext = /^image\.(\w+)$/i.exec(file.name)?.[1];
  if (!ext) return file;
  const d = new Date();
  const stamp = `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}-${pad(d.getHours())}${pad(d.getMinutes())}${pad(d.getSeconds())}`;
  return new File([file], `pasted-image-${stamp}.${ext.toLowerCase()}`, {
    type: file.type,
    lastModified: file.lastModified,
  });
}

// Files on the clipboard, or none when the paste is really text going into a
// text field (copying from Word puts a rendered bitmap beside the text).
export function filesFromClipboard(data: DataTransfer | null, target: EventTarget | null): File[] {
  if (!data || data.files.length === 0) return [];
  if (isTextEntry(target) && data.types.includes('text/plain')) return [];
  return Array.from(data.files).map(nameClipboardImage);
}

// Whether a picker's `accept` takes images, i.e. whether a "paste an image"
// hint is true for that slot. '' is the catch-all and takes anything.
export function acceptsImages(accept: string): boolean {
  return accept === '' || accept.split(',').some((token) => token.trim().startsWith('image/'));
}

export const PASTE_SHORTCUT =
  typeof navigator !== 'undefined' && /Macintosh|Mac OS X/.test(navigator.userAgent)
    ? '⌘V'
    : 'Ctrl+V';

// A phone or a keyboardless tablet has no hover and no paste shortcut outside a
// text field, so a paste hint there is an instruction the device cannot follow.
export const PASTE_HINT_SUPPORTED =
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(hover: hover) and (pointer: fine)').matches;

let hoveredTarget: symbol | null = null;

export interface PasteTargetProps {
  onPaste?: (event: ReactClipboardEvent<HTMLElement>) => void;
  onPointerEnter: () => void;
  onPointerLeave: () => void;
}

export function usePasteFiles(onFiles: (files: File[]) => void, enabled = true): PasteTargetProps {
  const onFilesRef = useRef(onFiles);
  const [id] = useState(() => Symbol('paste-target'));
  const [hovered, setHovered] = useState(false);
  // A disabled control may never see the pointer leave, and a stale hover
  // would route every later paste on the page here once it re-enables.
  if (!enabled && hovered) setHovered(false);

  useEffect(() => {
    onFilesRef.current = onFiles;
  }, [onFiles]);

  useEffect(() => {
    if (!enabled || !hovered) return;
    hoveredTarget = id;
    const listener = (event: ClipboardEvent): void => {
      if (event.defaultPrevented || isTextEntry(event.target)) return;
      const files = filesFromClipboard(event.clipboardData, event.target);
      if (files.length === 0) return;
      event.preventDefault();
      onFilesRef.current(files);
    };
    document.addEventListener('paste', listener);
    return () => {
      document.removeEventListener('paste', listener);
      if (hoveredTarget === id) hoveredTarget = null;
    };
  }, [enabled, hovered, id]);

  const pointer = {
    onPointerEnter: () => {
      if (enabled) setHovered(true);
    },
    onPointerLeave: () => setHovered(false),
  };
  if (!enabled) return pointer;
  return {
    ...pointer,
    onPaste: (event) => {
      if (event.defaultPrevented) return;
      if (hoveredTarget !== null && hoveredTarget !== id) return;
      const files = filesFromClipboard(event.clipboardData, event.target);
      if (files.length === 0) return;
      event.preventDefault();
      onFilesRef.current(files);
    },
  };
}
